import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Slider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  DirectionsRun,
  DirectionsWalk,
  FitnessCenter,
  History,
  Insights,
  LocalFireDepartment,
  NotificationsNone,
  Logout,
  Menu,
  MonitorHeart,
  RestaurantMenu,
  Settings,
  Spa,
  SupportAgent,
  Timeline,
  WaterDrop,
} from '@mui/icons-material';
import { addDays, format, isToday, isYesterday, parseISO, subDays } from 'date-fns';
import { todayDayKey } from '../utils/dateUtils';
import { resolveGoals } from '../utils/goalDefaults';
import { resolveMacroTargets } from '../utils/macroTargets';
import { useAuth } from '../contexts/AuthContext';
import { dailySummaryAPI, fastingV2API, foodsAPI, subscriptionsAPI, userAPI, userV2API } from '../services/api';
import FastingRecommendationsPanel from '../components/dashboard/FastingRecommendationsPanel';
import {
  summariesToHealthRange,
  summariesToMetricsRange,
  summaryToHealth,
  summaryToMetrics,
  summaryToReflection,
} from '../utils/dailySummaryAdapter';
import { tokens } from '../theme';
import { renderUserAnalyticsScreen, USER_ANALYTICS_SECTIONS } from '../components/dashboard/analytics/UserAnalyticsScreens';
import UserDashboardHome from '../components/dashboard/UserDashboardHome';
import ProductSubmissionDialog from '../components/dashboard/ProductSubmissionDialog';
import cardMountains from '../assets/card-mountains.png';

const USER_DASHBOARD_SECTIONS = ['daily-state', ...USER_ANALYTICS_SECTIONS];

const BG = tokens.bg;
const BORDER = 'rgba(197, 215, 152, 0.16)';
const BORDER_STRONG = 'rgba(197, 215, 152, 0.28)';
const RAIL_WIDTH = 220;
const FASTING_PROTOCOLS = [
  { value: '16:8', label: '16:8', hours: 16 },
  { value: '18:6', label: '18:6', hours: 18 },
  { value: '20:4', label: '20:4', hours: 20 },
  { value: '24:0', label: '24h', hours: 24 },
];
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks'];
const DEFAULT_REFLECTION = { notes: '', energy: 4, mood: 4, gratitude: '' };

const navItems = [
  { key: 'daily-state', label: 'Daily State', icon: <Spa fontSize="small" /> },
  { key: 'fasting-arc', label: 'Fasting Arc', icon: <Timeline fontSize="small" /> },
  { key: 'nutrition', label: 'Nutrition', icon: <RestaurantMenu fontSize="small" /> },
  { key: 'activity', label: 'Activity', icon: <DirectionsRun fontSize="small" /> },
  { key: 'biometrics', label: 'Biometrics', icon: <MonitorHeart fontSize="small" /> },
  { key: 'history', label: 'History', icon: <History fontSize="small" /> },
  { key: 'protocols', label: 'Protocols', icon: <Timeline fontSize="small" /> },
  { key: 'insights', label: 'Insights', icon: <Insights fontSize="small" /> },
  { key: 'support', label: 'Support', icon: <SupportAgent fontSize="small" /> },
  { key: 'settings', label: 'Settings', icon: <Settings fontSize="small" /> },
];

const editorialSerif = '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Georgia, serif';

function createPreviewDashboard(now) {
  const previewUser = {
    id: 'preview-user',
    name: 'Alexandra',
    email: 'demo@fasttime.com',
    weight: 69.1,
    bmi: 24.4,
    dailyCalorieRequirement: 2000,
    currentFastingStreak: 18,
    bestFastingStreak: 24,
    preferences: { useMetricSystem: false },
  };
  const previewDate = format(now, 'yyyy-MM-dd');
  const fastingStart = new Date(now.getTime() - ((10 * 60 + 26) * 60 + 37) * 1000);
  const fastingEnd = new Date(now.getTime() + ((5 * 60 + 34) * 60 + 11) * 1000);
  const previewEntries = [
    {
      id: 'preview-breakfast-1',
      foodId: 'preview-oats',
      foodName: 'Oats, Rolled',
      quantity: 150,
      unit: 'g',
      calories: 320,
      protein: 15,
      carbs: 40,
      fats: 7,
      fiber: 6,
      mealType: 'breakfast',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 30).toISOString(),
    },
    {
      id: 'preview-breakfast-2',
      foodId: 'preview-yogurt',
      foodName: 'Greek yogurt, berries, almonds, honey',
      quantity: 220,
      unit: 'g',
      calories: 260,
      protein: 22,
      carbs: 18,
      fats: 9,
      fiber: 4,
      mealType: 'breakfast',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 42).toISOString(),
    },
    {
      id: 'preview-lunch-1',
      foodId: 'preview-salmon',
      foodName: 'Grilled salmon, quinoa, asparagus',
      quantity: 340,
      unit: 'g',
      calories: 560,
      protein: 42,
      carbs: 38,
      fats: 21,
      fiber: 5,
      mealType: 'lunch',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 15).toISOString(),
    },
    {
      id: 'preview-dinner-1',
      foodId: 'preview-stirfry',
      foodName: 'Chicken stir fry, brown rice, vegetables',
      quantity: 390,
      unit: 'g',
      calories: 602,
      protein: 45,
      carbs: 44,
      fats: 26,
      fiber: 3,
      mealType: 'dinner',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 19, 10).toISOString(),
    },
    {
      id: 'preview-snack-1',
      foodId: 'preview-blueberries',
      foodName: 'Blueberries',
      quantity: 42,
      unit: 'kcal',
      calories: 42,
      protein: 1,
      carbs: 10,
      fats: 0,
      fiber: 2,
      mealType: 'snacks',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 15, 45).toISOString(),
    },
    {
      id: 'preview-snack-2',
      foodId: 'preview-avocado',
      foodName: 'Avocado',
      quantity: 80,
      unit: 'g',
      calories: 80,
      protein: 1,
      carbs: 4,
      fats: 7,
      fiber: 3,
      mealType: 'snacks',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 16, 30).toISOString(),
    },
    {
      id: 'preview-snack-3',
      foodId: 'preview-almonds',
      foodName: 'Almonds',
      quantity: 28,
      unit: 'g',
      calories: 164,
      protein: 6,
      carbs: 6,
      fats: 13,
      fiber: 3,
      mealType: 'snacks',
      timestamp: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 17, 5).toISOString(),
    },
  ];

  const previewMetric = {
    date: previewDate,
    fasting: {
      protocol: '16:8',
      startTime: fastingStart.toISOString(),
      plannedEndTime: fastingEnd.toISOString(),
      isActive: true,
      completed: false,
      endedEarly: false,
    },
    habits: {
      steps: 8642,
      waterIntake: 1.6,
      exerciseHours: 0.75,
      waterGoal: 2.5,
      stepsGoal: 10000,
      exerciseGoal: 1,
    },
    food: {
      foodEntries: previewEntries,
    },
  };

  const weeklyMetrics = Array.from({ length: 30 }).map((_, index) => {
    const dayOffset = 29 - index;
    const date = format(subDays(now, dayOffset), 'yyyy-MM-dd');
    if (dayOffset === 0) return previewMetric;
    const completedFast = dayOffset % 3 !== 0;
    return {
      date,
      fasting: completedFast
        ? {
            protocol: dayOffset % 5 === 0 ? '18:6' : '16:8',
            completed: true,
            isActive: false,
            actualFastingMinutes: dayOffset % 5 === 0 ? 17 * 60 : 15 * 60,
          }
        : null,
      habits: {
        steps: 6200 + ((index * 173) % 3200),
        waterIntake: 1.2 + ((index % 7) * 0.2),
        exerciseHours: 0.4 + ((index % 5) * 0.15),
        waterGoal: 2.5,
        stepsGoal: 10000,
        exerciseGoal: 1,
      },
      food: {
        foodEntries: [
          { calories: 1180 + ((index * 47) % 420), protein: 70 + (index % 20), carbs: 140 + (index % 30), fats: 45 + (index % 15), mealType: 'lunch' },
          { calories: 280 + (index % 120), protein: 12, carbs: 20, fats: 10, mealType: 'snacks' },
        ],
      },
      weight: 69.8 - index * 0.02,
    };
  });

  const healthDataRange = weeklyMetrics.map((metric, index) => ({
    date: metric.date,
    weight: metric.weight ?? 69.1,
    hrv: 40 + (index % 8),
    restingHeartRate: 62 - Math.floor(index / 6),
    sleepMinutes: 390 + ((index * 11) % 60),
    activeCaloriesBurned: 330 + ((index * 17) % 120),
  }));

  return {
    previewDate,
    previewUser,
    previewMetric,
    weeklyMetrics,
    healthDataRange,
    isPremium: true,
    recentFoods: [
      previewEntries[0],
      previewEntries[4],
      previewEntries[5],
      previewEntries[6],
    ],
  };
}

function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

function formatDecimal(value, digits = 1) {
  if (!Number.isFinite(value)) return '0';
  return Number(value).toFixed(digits);
}

function formatFriendlyDate(selectedDate) {
  const selectedDateObj = parseISO(selectedDate);
  if (isToday(selectedDateObj)) return 'Today';
  if (isYesterday(selectedDateObj)) return 'Yesterday';
  return format(selectedDateObj, 'EEE, MMM d');
}

function getGreeting(name) {
  const hour = new Date().getHours();
  if (hour < 12) return `Good morning, ${name}.`;
  if (hour < 18) return `Good afternoon, ${name}.`;
  return `Good evening, ${name}.`;
}

function getFastingState(fasting, now) {
  if (!fasting || !fasting.startTime || !fasting.protocol) {
    return {
      isActive: false,
      progress: 0,
      countdownText: 'Not started',
      phaseLabel: 'Ready when you are',
      phaseCopy: 'Choose a protocol to begin your next fasting window.',
      timeRemainingLabel: 'Plan your next window',
      windowLabel: 'No active fast',
      startedLabel: 'No start time logged yet',
    };
  }

  const protocol = FASTING_PROTOCOLS.find((item) => item.value === fasting.protocol);
  const plannedHours = protocol?.hours || 16;
  const start = new Date(fasting.startTime);
  const plannedEnd = fasting.plannedEndTime ? new Date(fasting.plannedEndTime) : new Date(start.getTime() + plannedHours * 60 * 60 * 1000);
  const totalMs = Math.max(plannedEnd - start, 1);
  const elapsedMs = Math.max(now - start, 0);
  const remainingMs = Math.max(plannedEnd - now, 0);
  const progress = Math.min((elapsedMs / totalMs) * 100, 100);
  const elapsedHours = elapsedMs / (60 * 60 * 1000);

  let phaseLabel = 'Cleansing';
  let phaseCopy = 'Settling into the fasting window.';
  if (elapsedHours >= 4 && elapsedHours < 12) {
    phaseLabel = 'Fat burning';
    phaseCopy = 'Your body is leaning more on stored energy.';
  } else if (elapsedHours >= 12 && elapsedHours < 16) {
    phaseLabel = 'Deep ketosis';
    phaseCopy = 'Momentum is steady and mental clarity often rises here.';
  } else if (elapsedHours >= 16) {
    phaseLabel = 'Cellular repair';
    phaseCopy = 'You are deep into the intentional stretch of the fast.';
  }

  return {
    isActive: fasting.isActive,
    progress,
    countdownText: fasting.isActive ? formatDuration(remainingMs) : `${Math.round((fasting.actualFastingMinutes || 0) / 60)}h completed`,
    phaseLabel,
    phaseCopy,
    timeRemainingLabel: fasting.isActive ? 'Time remaining' : 'Completed window',
    windowLabel: `${fasting.protocol} protocol`,
    startedLabel: formatFastingStartedLabel(start, now),
    plannedEnd,
  };
}

function formatFastingStartedLabel(start, now) {
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - startDay) / (1000 * 60 * 60 * 24));
  const timeLabel = format(start, 'h:mm a').toLowerCase();
  if (diffDays === 1) return `Started ${timeLabel} yesterday`;
  if (diffDays > 1) return `Started ${timeLabel} ${diffDays} days ago`;
  return `Started ${timeLabel}`;
}

function formatWeightValue(weightKg, useMetricSystem) {
  if (!Number.isFinite(weightKg)) return '—';
  if (useMetricSystem) return `${formatDecimal(weightKg)} kg`;
  return `${formatDecimal(weightKg * 2.20462, 1)} lbs`;
}

function formatWeightDelta(currentKg, previousKg, useMetricSystem) {
  if (!Number.isFinite(currentKg) || !Number.isFinite(previousKg)) return null;
  const deltaKg = currentKg - previousKg;
  if (Math.abs(deltaKg) < 0.05) return null;
  const sign = deltaKg > 0 ? '↑' : '↓';
  if (useMetricSystem) {
    return `${sign} ${formatDecimal(Math.abs(deltaKg), 1)} kg`;
  }
  return `${sign} ${formatDecimal(Math.abs(deltaKg * 2.20462), 1)} lbs`;
}

function formatSleepDuration(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours} h ${mins} m`;
}

function formatDelta(current, previous, { suffix = '', invert = false, percent = false } = {}) {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  const rawDelta = current - previous;
  if (Math.abs(rawDelta) < 0.05) return null;
  const delta = invert ? -rawDelta : rawDelta;
  const sign = delta > 0 ? '↑' : '↓';
  if (percent && previous !== 0) {
    const pct = Math.round((Math.abs(delta) / Math.abs(previous)) * 100);
    return `${sign} ${pct}%`;
  }
  const magnitude = Math.abs(delta);
  const formatted = Number.isInteger(magnitude) ? magnitude : formatDecimal(magnitude, 1);
  return `${sign} ${formatted}${suffix}`;
}

function getMacroTotals(foodEntries) {
  return foodEntries.reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fats: acc.fats + (entry.fats || 0),
      fiber: acc.fiber + (entry.fiber || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0 }
  );
}

function hasMeaningfulMetrics(metric) {
  if (!metric) return false;
  const entries = metric.food?.foodEntries || [];
  const habits = metric.habits || {};
  return Boolean(
    entries.length ||
    metric.fasting?.isActive ||
    metric.fasting?.completed ||
    habits.waterIntake ||
    habits.steps ||
    habits.exerciseHours
  );
}

function hasMeaningfulSummary(summary) {
  if (!summary) return false;
  const nutrition = summary.nutrition || {};
  const manualMetrics = summary.manualMetrics || {};
  const importedMetrics = summary.importedMetrics || {};
  const wellbeing = summary.wellbeing || {};
  const bodyMetrics = summary.bodyMetrics || {};

  return Boolean(
    (nutrition.foodEntries || []).length ||
    summary.fasting?.isActive ||
    summary.fasting?.completed ||
    manualMetrics.waterIntake ||
    manualMetrics.steps ||
    manualMetrics.exerciseHours ||
    importedMetrics.waterIntake ||
    importedMetrics.steps ||
    importedMetrics.exerciseHours ||
    importedMetrics.caloriesBurned ||
    importedMetrics.sleepMinutes ||
    importedMetrics.heartRate ||
    wellbeing.notes?.trim() ||
    wellbeing.gratitude?.trim() ||
    wellbeing.energy ||
    wellbeing.mood ||
    bodyMetrics.weight ||
    bodyMetrics.restingHeartRate ||
    bodyMetrics.hrv ||
    bodyMetrics.bloodGlucose ||
    bodyMetrics.bloodOxygen ||
    bodyMetrics.bloodPressure?.systolic ||
    bodyMetrics.bloodPressure?.diastolic ||
    summary.notes?.trim()
  );
}

function getMealGroups(foodEntries) {
  return MEAL_TYPES.map((mealType) => ({
    mealType,
    label: mealType.charAt(0).toUpperCase() + mealType.slice(1),
    entries: foodEntries
      .filter((entry) => entry.mealType === mealType)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  })).filter((group) => group.entries.length > 0);
}

function summarizeMealEntries(entries) {
  if (!entries?.length) return 'Nothing logged yet.';
  const summary = entries
    .slice(0, 2)
    .map((entry) => entry.foodName)
    .join(', ');
  return summary.length > 58 ? `${summary.slice(0, 55)}...` : summary;
}

function getFoodVisual(entry) {
  const name = (entry?.foodName || '').toLowerCase();

  if (name.includes('oat') || name.includes('yogurt')) {
    return { glyph: '🥣', bg: 'radial-gradient(circle at 35% 35%, #f0d8ba 0%, #b88250 58%, #3b2e22 100%)' };
  }
  if (name.includes('blueberr')) {
    return { glyph: '🫐', bg: 'radial-gradient(circle at 35% 35%, #b8c4ff 0%, #5265aa 52%, #1c2238 100%)' };
  }
  if (name.includes('avocado')) {
    return { glyph: '🥑', bg: 'radial-gradient(circle at 35% 35%, #ebd98a 0%, #78a062 48%, #28331f 100%)' };
  }
  if (name.includes('almond') || name.includes('nut')) {
    return { glyph: '🥜', bg: 'radial-gradient(circle at 35% 35%, #d8aa76 0%, #8f5f38 58%, #352619 100%)' };
  }

  return { glyph: '🍽️', bg: 'radial-gradient(circle at 35% 35%, #d7c486 0%, #7b8f5c 55%, #283124 100%)' };
}

function getChartData(metricsRange, fallbackWeight) {
  return metricsRange.map((metric) => {
    const entries = metric.food?.foodEntries || [];
    return {
      date: format(new Date(metric.date), 'MMM d'),
      steps: metric.habits?.steps || 0,
      calories: entries.reduce((sum, item) => sum + (item.calories || 0), 0),
      water: metric.habits?.waterIntake || 0,
      weight: metric.weight || fallbackWeight || 0,
    };
  });
}

function tonalButtonSx() {
  return {
    justifyContent: 'space-between',
    px: 1.5,
    py: 0.95,
    borderRadius: 2,
    borderColor: BORDER,
    color: tokens.textSoft,
    backgroundColor: 'rgba(255,255,255,0.02)',
    '&:hover': {
      borderColor: BORDER_STRONG,
      backgroundColor: 'rgba(255,255,255,0.04)',
    },
  };
}

function DashboardHistoryNavigator({
  selectedDate,
  selectedDateLabel,
  historyDays,
  onChangeDate,
  onStepDate,
  loading,
}) {
  const canMoveForward = selectedDate < todayDayKey;

  return (
    <Box
      sx={{
        mb: 2.1,
        p: { xs: 1.4, md: 1.75 },
        border: `1px solid ${BORDER}`,
        background: 'linear-gradient(135deg, rgba(13, 22, 18, 0.96) 0%, rgba(22, 33, 25, 0.9) 100%)',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={{ xs: 1.4, lg: 2 }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
      >
        <Box>
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.45 }}>
            <CalendarMonth sx={{ color: tokens.accentStrong, fontSize: 18 }} />
            <Typography variant="overline" sx={{ color: tokens.accentStrong, letterSpacing: '0.08em' }}>
              History Controls
            </Typography>
          </Stack>
          <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500 }}>
            Review and edit any day from the dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.4 }}>
            Selected day: {selectedDateLabel} ({selectedDate})
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => onStepDate(-1)}
              disabled={loading}
              sx={{ border: `1px solid ${BORDER}`, color: tokens.textSoft, borderRadius: 2 }}
              aria-label="Previous day"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => onStepDate(1)}
              disabled={loading || !canMoveForward}
              sx={{ border: `1px solid ${BORDER}`, color: tokens.textSoft, borderRadius: 2 }}
              aria-label="Next day"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
          <TextField
            label="Jump to date"
            type="date"
            value={selectedDate}
            onChange={(event) => onChangeDate(event.target.value)}
            disabled={loading}
            inputProps={{ max: todayDayKey }}
            sx={{
              minWidth: { xs: '100%', sm: 188 },
              '& .MuiInputBase-root': {
                color: tokens.cream,
                backgroundColor: 'rgba(255,255,255,0.03)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: BORDER,
              },
              '& .MuiInputLabel-root': {
                color: tokens.textMuted,
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            onClick={() => onChangeDate(todayDayKey)}
            disabled={loading || selectedDate === todayDayKey}
            sx={{ ...tonalButtonSx(), justifyContent: 'center', px: 2.1, minWidth: 110 }}
          >
            Back to today
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.4, overflowX: 'auto', pb: 0.25 }}>
        {historyDays.map((day) => (
          <Button
            key={day.dayKey}
            onClick={() => onChangeDate(day.dayKey)}
            disabled={loading}
            variant={day.dayKey === selectedDate ? 'contained' : 'outlined'}
            sx={{
              flexShrink: 0,
              minWidth: 88,
              px: 1.2,
              py: 1,
              borderRadius: 2,
              borderColor: day.dayKey === selectedDate ? 'transparent' : BORDER,
              background: day.dayKey === selectedDate
                ? 'linear-gradient(135deg, rgba(169,191,122,0.22) 0%, rgba(124,146,81,0.3) 100%)'
                : 'rgba(255,255,255,0.02)',
              color: tokens.cream,
              textTransform: 'none',
              '&:hover': {
                borderColor: BORDER_STRONG,
                backgroundColor: day.dayKey === selectedDate ? undefined : 'rgba(255,255,255,0.04)',
              },
            }}
          >
            <Stack spacing={0.15} alignItems="flex-start">
              <Typography variant="caption" sx={{ color: day.dayKey === selectedDate ? tokens.cream : tokens.textSoft }}>
                {day.weekday}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.cream }}>
                {day.label}
              </Typography>
              <Typography variant="caption" sx={{ color: day.hasData ? tokens.accentStrong : tokens.textMuted }}>
                {day.hasData ? 'Logged' : day.isToday ? 'Today' : 'Open'}
              </Typography>
            </Stack>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

export default function UserDashboardEnhanced() {
  const navigate = useNavigate();
  const location = useLocation();
  const { section: sectionParam } = useParams();
  const activeSection = USER_DASHBOARD_SECTIONS.includes(sectionParam) ? sectionParam : null;
  const { user, logout, previewMode } = useAuth();
  const isDesktop = useMediaQuery('(min-width:820px)');
  const isWideBoard = useMediaQuery('(min-width:1100px)');
  const [userData, setUserData] = useState(null);
  const [todayMetrics, setTodayMetrics] = useState(null);
  const [weeklyMetrics, setWeeklyMetrics] = useState([]);
  const [recentFoods, setRecentFoods] = useState([]);
  const [selectedDate, setSelectedDate] = useState(todayDayKey);
  const [habitsDialogOpen, setHabitsDialogOpen] = useState(false);
  const [habitDraft, setHabitDraft] = useState({ steps: 0, waterIntake: 0, exerciseHours: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [foodDialogOpen, setFoodDialogOpen] = useState(false);
  const [productSubmissionOpen, setProductSubmissionOpen] = useState(false);
  const [fastingDialogOpen, setFastingDialogOpen] = useState(false);
  const [foods, setFoods] = useState([]);
  const [foodSearch, setFoodSearch] = useState('');
  const [selectedFood, setSelectedFood] = useState(null);
  const [foodQuantity, setFoodQuantity] = useState(100);
  const [foodMealType, setFoodMealType] = useState('snacks');
  const [foodContextDraft, setFoodContextDraft] = useState({
    mealContext: '',
    satietyScore: 5,
    cravingTrigger: '',
    preparationType: '',
    diningContext: '',
    servingConfidence: 0.8,
  });
  const [fastingProtocol, setFastingProtocol] = useState('16:8');
  const [fastingRecommendations, setFastingRecommendations] = useState(null);
  const [nutritionPreferences, setNutritionPreferences] = useState(null);
  const [fastQualityDraft, setFastQualityDraft] = useState({
    hungerScore: 5,
    energyScore: 5,
    focusScore: 5,
    cravingScore: 5,
    sleepImpact: 'none',
    workoutImpact: 'none',
    reasonEndedEarly: '',
  });
  const [updating, setUpdating] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [reflection, setReflection] = useState({ notes: '', energy: 4, mood: 4, gratitude: '' });
  const [healthDataRange, setHealthDataRange] = useState([]);
  const [summaryRange, setSummaryRange] = useState([]);
  const [todayHealthData, setTodayHealthData] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const latestMeaningfulMetric = useMemo(() => {
    const ordered = [...weeklyMetrics].reverse();
    return ordered.find(hasMeaningfulMetrics) || null;
  }, [weeklyMetrics]);

  const selectedDateHasContent = useMemo(
    () => hasMeaningfulSummary(selectedSummary),
    [selectedSummary]
  );

  const historySummaryByDay = useMemo(
    () => new Map(summaryRange.map((summary) => [summary.dayKey, summary])),
    [summaryRange]
  );

  const displayMetric = useMemo(() => {
    if (selectedDate !== todayDayKey) return todayMetrics;
    if (hasMeaningfulMetrics(todayMetrics) || selectedDateHasContent) return todayMetrics;
    return latestMeaningfulMetric || todayMetrics;
  }, [latestMeaningfulMetric, selectedDate, selectedDateHasContent, todayMetrics]);

  const displayDate = useMemo(() => {
    if (selectedDate !== todayDayKey) return selectedDate;
    if (hasMeaningfulMetrics(todayMetrics) || selectedDateHasContent) return selectedDate;
    if (latestMeaningfulMetric?.date) return format(new Date(latestMeaningfulMetric.date), 'yyyy-MM-dd');
    return selectedDate;
  }, [latestMeaningfulMetric, selectedDate, selectedDateHasContent, todayMetrics]);

  const selectedDateLabel = useMemo(() => formatFriendlyDate(selectedDate), [selectedDate]);
  const historyDays = useMemo(() => {
    const baseDate = parseISO(selectedDate);
    return Array.from({ length: 7 }).map((_, index) => {
      const date = subDays(baseDate, 6 - index);
      const dayKey = format(date, 'yyyy-MM-dd');
      const summary = dayKey === selectedDate ? selectedSummary : historySummaryByDay.get(dayKey);
      return {
        dayKey,
        weekday: format(date, 'EEE'),
        label: formatFriendlyDate(dayKey),
        isToday: dayKey === todayDayKey,
        hasData: hasMeaningfulSummary(summary),
      };
    });
  }, [historySummaryByDay, selectedDate, selectedSummary]);

  const loadDashboard = useCallback(async () => {
    if (previewMode) {
      const preview = createPreviewDashboard(new Date());
      setUserData(preview.previewUser);
      setTodayMetrics(preview.previewMetric);
      setWeeklyMetrics(preview.weeklyMetrics);
      setHealthDataRange(preview.healthDataRange);
      setTodayHealthData(preview.healthDataRange[preview.healthDataRange.length - 1]);
      setIsPremium(preview.isPremium);
      setRecentFoods(preview.recentFoods);
      setSelectedDate(preview.previewDate);
      setSelectedSummary(null);
      setReflection({
        notes: 'Felt energized through the morning fast. Lunch was satisfying and kept me steady through the afternoon.',
        energy: 4,
        mood: 4,
        gratitude: 'My health, time in nature, and supportive friends.',
      });
      setLoading(false);
      return;
    }

    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const endDate = selectedDate;
      const startDate = format(subDays(parseISO(selectedDate), 29), 'yyyy-MM-dd');
      const [userRes, summaryRes, rangeRes, subscriptionRes] = await Promise.all([
        userAPI.get(user.id),
        dailySummaryAPI.get(user.id, selectedDate).catch(() => ({ data: { data: null } })),
        dailySummaryAPI.getRange(user.id, startDate, endDate).catch(() => ({ data: { data: [] } })),
        subscriptionsAPI.getMe().catch(() => ({ data: { data: { isPremium: false } } })),
      ]);

      const userPayload = userRes.data?.data || null;
      const todaySummary = summaryRes.data?.data || null;
      const summaries = Array.isArray(rangeRes.data?.data) ? rangeRes.data.data : [];
      const metricsPayload = summaryToMetrics(todaySummary);
      const rangePayload = summariesToMetricsRange(summaries);
      const healthPayload = summaryToHealth(todaySummary);
      const healthRangePayload = summariesToHealthRange(summaries);

      setUserData(userPayload);
      setSelectedSummary(todaySummary);
      setTodayMetrics(metricsPayload);
      setWeeklyMetrics(rangePayload);
      setTodayHealthData(healthPayload);
      setHealthDataRange(healthRangePayload);
      setSummaryRange(summaries);
      setIsPremium(Boolean(subscriptionRes.data?.data?.isPremium));
      setReflection(todaySummary ? summaryToReflection(todaySummary) : DEFAULT_REFLECTION);

      const allFoods = metricsPayload?.food?.foodEntries || [];
      const seen = new Set();
      const recents = [];
      for (const entry of [...allFoods].reverse()) {
        if (seen.has(entry.foodId)) continue;
        seen.add(entry.foodId);
        recents.push(entry);
        if (recents.length >= 6) break;
      }
      setRecentFoods(recents);
    } catch (err) {
      setError(err.message || 'Failed to load your dashboard');
    } finally {
      setLoading(false);
    }
  }, [previewMode, selectedDate, user]);

  const loadFoods = useCallback(async () => {
    try {
      const response = await foodsAPI.list({ query: foodSearch || undefined, limit: 50 });
      setFoods(response.data?.data?.items || []);
    } catch {
      setFoods([]);
    }
  }, [foodSearch]);

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user, loadDashboard]);

  useEffect(() => {
    if (!user || previewMode) return undefined;
    let cancelled = false;

    const loadRecommendations = async () => {
      try {
        const res = await fastingV2API.getRecommendations(user.id, { lookbackDays: 14 });
        if (!cancelled) {
          setFastingRecommendations(res.data?.data || null);
        }
      } catch {
        if (!cancelled) setFastingRecommendations(null);
      }
    };

    loadRecommendations();
    return () => { cancelled = true; };
  }, [previewMode, user]);

  useEffect(() => {
    if (!user?.id || previewMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await userV2API.getNutritionPreferences(user.id);
        if (!cancelled) setNutritionPreferences(res.data?.data ?? null);
      } catch {
        if (!cancelled) setNutritionPreferences(null);
      }
    })();
    return () => { cancelled = true; };
  }, [previewMode, user?.id]);

  useEffect(() => {
    if (todayMetrics?.fasting?.isActive) return;
    const suggested = fastingRecommendations?.suggestedProtocol;
    if (suggested) setFastingProtocol(suggested);
  }, [fastingRecommendations?.suggestedProtocol, todayMetrics?.fasting?.isActive]);

  useEffect(() => {
    if (!foodDialogOpen) return;
    loadFoods();
  }, [foodDialogOpen, loadFoods]);

  useEffect(() => {
    const activeFast = todayMetrics?.fasting?.isActive;
    if (!activeFast) return undefined;
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [todayMetrics?.fasting?.isActive]);

  useEffect(() => {
    if (!user || previewMode) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        await dailySummaryAPI.patchSection(user.id, selectedDate, 'wellbeing', {
          notes: reflection.notes,
          energy: reflection.energy,
          mood: reflection.mood,
          gratitude: reflection.gratitude,
        });
      } catch {
        // Non-blocking save for reflection journal
      }
    }, 600);
    return () => window.clearTimeout(timer);
  }, [previewMode, reflection, selectedDate, user]);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSelectedDateChange = useCallback((nextDate) => {
    if (!nextDate) return;
    const parsed = parseISO(nextDate);
    if (Number.isNaN(parsed.getTime())) {
      showSnackbar('Choose a valid date.', 'error');
      return;
    }
    const safeDate = nextDate > todayDayKey ? todayDayKey : nextDate;
    setSelectedDate(safeDate);
  }, [showSnackbar]);

  const handleStepSelectedDate = useCallback((direction) => {
    const nextDate = format(addDays(parseISO(selectedDate), direction), 'yyyy-MM-dd');
    handleSelectedDateChange(nextDate);
  }, [handleSelectedDateChange, selectedDate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const navigateToSection = useCallback((key) => {
    // Preserve the query string (in particular ?preview=1) so the URL stays
    // shareable and refreshable. previewMode itself now comes from
    // AuthContext (captured once at session establishment), not re-derived
    // from window.location.search on every render, so this is belt-and-
    // braces rather than the load-bearing fix -- but a preview link that
    // goes dead on refresh after one click is still worth avoiding.
    navigate({ pathname: `/dashboard/${key}`, search: location.search });
    setMobileDrawerOpen(false);
  }, [location.search, navigate]);

  const handleAddFood = useCallback(async () => {
    if (!user || !selectedFood) {
      showSnackbar('Select a food first.', 'error');
      return;
    }

    setUpdating(true);
    try {
      const factor = foodQuantity / (selectedFood.quantity || 100);
      const entry = {
        id: `entry_${Date.now()}`,
        foodId: selectedFood.id,
        foodName: selectedFood.name,
        quantity: foodQuantity,
        unit: selectedFood.unit || 'g',
        calories: Math.round((selectedFood.calories || 0) * factor),
        protein: selectedFood.protein != null ? Number((selectedFood.protein * factor).toFixed(1)) : null,
        carbs: selectedFood.carbs != null ? Number((selectedFood.carbs * factor).toFixed(1)) : null,
        fats: selectedFood.fats != null ? Number((selectedFood.fats * factor).toFixed(1)) : null,
        fiber: selectedFood.fiber != null ? Number((selectedFood.fiber * factor).toFixed(1)) : null,
        timestamp: new Date().toISOString(),
        mealType: foodMealType,
        ...(foodContextDraft.mealContext ? { mealContext: foodContextDraft.mealContext } : {}),
        ...(foodContextDraft.satietyScore ? { satietyScore: foodContextDraft.satietyScore } : {}),
        ...(foodContextDraft.cravingTrigger ? { cravingTrigger: foodContextDraft.cravingTrigger } : {}),
        ...(foodContextDraft.preparationType ? { preparationType: foodContextDraft.preparationType } : {}),
        ...(foodContextDraft.diningContext ? { diningContext: foodContextDraft.diningContext } : {}),
        servingConfidence: foodContextDraft.servingConfidence,
      };
      await dailySummaryAPI.addFood(user.id, selectedDate, entry);
      setTodayMetrics((prev) => ({
        ...prev,
        food: {
          ...(prev?.food || {}),
          foodEntries: [...(prev?.food?.foodEntries || []), entry],
        },
      }));
      setRecentFoods((prev) => [entry, ...prev.filter((item) => item.foodId !== entry.foodId)].slice(0, 6));
      setFoodDialogOpen(false);
      setSelectedFood(null);
      setFoodSearch('');
      setFoodQuantity(100);
      setFoodMealType('snacks');
      setFoodContextDraft({
        mealContext: '',
        satietyScore: 5,
        cravingTrigger: '',
        preparationType: '',
        diningContext: '',
        servingConfidence: 0.8,
      });
      showSnackbar(`${selectedFood.name} added to ${foodMealType}.`);
      loadDashboard();
    } catch (err) {
      showSnackbar(err.message || 'Unable to add food.', 'error');
    } finally {
      setUpdating(false);
    }
  }, [foodContextDraft, foodMealType, foodQuantity, loadDashboard, selectedDate, selectedFood, showSnackbar, user]);

  const handleQuickAddRecentFood = useCallback(async (food) => {
    if (!user) return;
    setUpdating(true);
    try {
      const entry = {
        ...food,
        id: `entry_${Date.now()}`,
        timestamp: new Date().toISOString(),
      };
      await dailySummaryAPI.addFood(user.id, selectedDate, entry);
      showSnackbar(`${food.foodName} logged again.`);
      loadDashboard();
    } catch (err) {
      showSnackbar(err.message || 'Unable to log that food.', 'error');
    } finally {
      setUpdating(false);
    }
  }, [loadDashboard, selectedDate, showSnackbar, user]);

  const handleStartOrStopFast = useCallback(async () => {
    if (!user) return;
    setUpdating(true);
    try {
      const currentFast = todayMetrics?.fasting;
      if (currentFast?.isActive) {
        const endTime = new Date();
        const startTime = currentFast.startTime ? new Date(currentFast.startTime) : endTime;
        const endedEarly = endTime < new Date(currentFast.plannedEndTime || endTime);
        await dailySummaryAPI.patchSection(user.id, selectedDate, 'fasting', {
          ...currentFast,
          endTime: endTime.toISOString(),
          isActive: false,
          completed: true,
          endedEarly,
          actualFastingMinutes: Math.round((endTime - startTime) / 60000),
          hungerScore: fastQualityDraft.hungerScore,
          energyScore: fastQualityDraft.energyScore,
          focusScore: fastQualityDraft.focusScore,
          cravingScore: fastQualityDraft.cravingScore,
          sleepImpact: fastQualityDraft.sleepImpact,
          workoutImpact: fastQualityDraft.workoutImpact,
          reasonEndedEarly: endedEarly ? (fastQualityDraft.reasonEndedEarly || null) : null,
        });
        showSnackbar('Fast ended.');
      } else {
        const protocol = FASTING_PROTOCOLS.find((item) => item.value === fastingProtocol) || FASTING_PROTOCOLS[0];
        const startTime = new Date();
        const plannedEndTime = new Date(startTime.getTime() + protocol.hours * 60 * 60 * 1000);
        await dailySummaryAPI.patchSection(user.id, selectedDate, 'fasting', {
          protocol: protocol.value,
          startTime: startTime.toISOString(),
          plannedEndTime: plannedEndTime.toISOString(),
          isActive: true,
          completed: false,
          endedEarly: false,
        });
        await userV2API.patchFastingProfile(user.id, {
          preferredProtocols: [protocol.value],
        }).catch(() => {});
        showSnackbar(`Started ${protocol.label} fast.`);
      }
      setFastingDialogOpen(false);
      loadDashboard();
    } catch (err) {
      showSnackbar(err.message || 'Unable to update fasting session.', 'error');
    } finally {
      setUpdating(false);
    }
  }, [fastQualityDraft, fastingProtocol, loadDashboard, selectedDate, showSnackbar, todayMetrics?.fasting, user]);

  const openHabitsDialog = useCallback(() => {
    const currentHabits = todayMetrics?.habits || displayMetric?.habits || {};
    setHabitDraft({
      steps: currentHabits.steps || 0,
      waterIntake: currentHabits.waterIntake || 0,
      exerciseHours: currentHabits.exerciseHours || 0,
    });
    setHabitsDialogOpen(true);
  }, [displayMetric?.habits, todayMetrics?.habits]);

  const handleSaveReflection = useCallback(async () => {
    if (!user || previewMode) return;
    setUpdating(true);
    try {
      await dailySummaryAPI.patchSection(user.id, selectedDate, 'wellbeing', {
        notes: reflection.notes,
        energy: reflection.energy,
        mood: reflection.mood,
        gratitude: reflection.gratitude,
      });
      showSnackbar('Reflection saved');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save reflection', 'error');
    } finally {
      setUpdating(false);
    }
  }, [previewMode, reflection, selectedDate, showSnackbar, user]);

  const handleRemoveFood = useCallback(async (entryId) => {
    if (!user || previewMode) return;
    setUpdating(true);
    try {
      await dailySummaryAPI.removeFood(user.id, selectedDate, entryId);
      showSnackbar('Food entry removed');
      await loadDashboard();
    } catch (err) {
      showSnackbar(err.message || 'Unable to remove food entry', 'error');
    } finally {
      setUpdating(false);
    }
  }, [loadDashboard, previewMode, selectedDate, showSnackbar, user]);

  const handleSaveHabits = useCallback(async () => {
    if (!user) return;
    setUpdating(true);
    try {
      const { waterGoal, stepsGoal, exerciseGoal } = resolveGoals({
        habits: todayMetrics?.habits,
        preferences: userData?.preferences,
      });
      await dailySummaryAPI.patchSection(user.id, selectedDate, 'manualMetrics', {
        steps: Number(habitDraft.steps) || 0,
        waterIntake: Number(habitDraft.waterIntake) || 0,
        exerciseHours: Number(habitDraft.exerciseHours) || 0,
        waterGoal,
        stepsGoal,
        exerciseGoal,
      });
      setHabitsDialogOpen(false);
      await loadDashboard();
      showSnackbar('Activity logged for this day.', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Unable to save activity.', 'error');
    } finally {
      setUpdating(false);
    }
  }, [habitDraft, loadDashboard, selectedDate, showSnackbar, todayMetrics?.habits, user, userData?.preferences]);

  const friendlyName = userData?.name || user?.email?.split('@')[0] || 'friend';
  const habits = displayMetric?.habits || {};
  const foodEntries = displayMetric?.food?.foodEntries || [];
  const macroTotals = getMacroTotals(foodEntries);
  const mealGroups = getMealGroups(foodEntries);
  const fastingState = getFastingState(displayMetric?.fasting, now);
  const fastingStreak = userData?.currentFastingStreak || 0;
  const bestFastingStreak = Math.max(userData?.bestFastingStreak || 0, fastingStreak);
  // No `|| 2000`: the API returns null when the profile lacks the measurements
  // to compute a target, and showing a made-up 2000 as "your goal" is worse
  // than showing nothing.
  const dailyCalorieRequirement = userData?.dailyCalorieRequirement ?? null;
  const macroTargets = resolveMacroTargets({
    calorieGoal: dailyCalorieRequirement,
    nutritionPreferences,
  });
  const useMetricSystem = userData?.preferences?.useMetricSystem !== false;
  const chartData = getChartData(weeklyMetrics, userData?.weight);
  const { waterGoal, stepsGoal: stepGoal, exerciseGoal: exerciseGoalHours } = resolveGoals({
    habits,
    preferences: userData?.preferences,
  });
  const exerciseMinutes = Math.round((habits.exerciseHours || 0) * 60);
  const exerciseGoalMinutes = Math.round(exerciseGoalHours * 60);
  const activeCalories = todayHealthData?.activeCaloriesBurned || 0;
  const biometricSeries = (healthDataRange.length ? healthDataRange : weeklyMetrics).map((entry, index) => ({
    idx: index,
    // No `?? 0`: a profile without a weight must render '—' via
    // formatWeightValue's Number.isFinite check, not a fabricated "0.0 kg"
    // with a sparkline flat-lined at zero. Matches the sibling fields below
    // (hrv, restingHeartRate, sleepMinutes) and computeBiometricsAnalytics's
    // treatment of the same value in userAnalytics.js.
    weight: entry.weight ?? userData?.weight ?? null,
    hrv: entry.hrv ?? null,
    restingHeartRate: entry.restingHeartRate ?? entry.heartRate ?? null,
    sleepMinutes: entry.sleepMinutes ?? null,
  }));
  const latestBiometric = biometricSeries[biometricSeries.length - 1] || {};
  const previousBiometric = biometricSeries[biometricSeries.length - 2] || {};
  const fastingPhases = [
    { label: 'Cleansing', window: '0 - 4h' },
    { label: 'Fat Burning', window: '4 - 12h' },
    { label: 'Deep Ketosis', window: '12 - 16h' },
    { label: 'Cellular Repair', window: '16 - 24h' },
  ];
  const activeFastingPhaseIndex = Math.max(
    fastingPhases.findIndex((phase) => phase.label.toLowerCase() === fastingState.phaseLabel.toLowerCase()),
    0
  );
  const mealShowcase = mealGroups.filter((group) => group.mealType !== 'snacks').slice(0, 3);
  const nutritionMacroCards = [
    ['Calories', macroTotals.calories, dailyCalorieRequirement, 'kcal', tokens.accentStrong],
    ['Protein', macroTotals.protein, macroTargets.protein, 'g', '#a6c26f'],
    ['Carbs', macroTotals.carbs, macroTargets.carbs, 'g', '#cfb66c'],
    ['Fats', macroTotals.fats, macroTargets.fats, 'g', '#d58a5f'],
    ['Fiber', macroTotals.fiber, macroTargets.fiber, 'g', '#8ea665'],
  ];
  const estimatedEndText = displayMetric?.fasting?.plannedEndTime
    ? `${format(new Date(displayMetric.fasting.plannedEndTime), 'EEEE')} ${format(new Date(displayMetric.fasting.plannedEndTime), 'h:mm a')}`
    : 'Choose a protocol to begin';
  const activityCards = [
    {
      label: 'Steps',
      value: habits.steps || 0,
      suffix: '',
      goal: `Goal ${stepGoal.toLocaleString()}`,
      tone: tokens.accent,
      icon: <DirectionsWalk sx={{ color: '#dbc56f', fontSize: 18 }} />,
      iconBg: 'rgba(219, 197, 111, 0.12)',
      progress: Math.min(((habits.steps || 0) / stepGoal) * 100, 100),
    },
    {
      label: 'Water',
      value: formatDecimal(habits.waterIntake || 0),
      suffix: 'L',
      goal: `Goal ${formatDecimal(waterGoal)} L`,
      tone: '#8ec8d7',
      icon: <WaterDrop sx={{ color: '#77c1d4', fontSize: 18 }} />,
      iconBg: 'rgba(119, 193, 212, 0.12)',
      progress: Math.min(((habits.waterIntake || 0) / waterGoal) * 100, 100),
    },
    {
      label: 'Exercise',
      value: exerciseMinutes,
      suffix: 'min',
      goal: `Goal ${exerciseGoalMinutes} min`,
      tone: tokens.clay,
      icon: <FitnessCenter sx={{ color: '#d6a16d', fontSize: 17 }} />,
      iconBg: 'rgba(214, 161, 109, 0.12)',
      progress: Math.min((exerciseMinutes / Math.max(exerciseGoalMinutes, 1)) * 100, 100),
    },
    {
      label: 'Active Calories',
      value: Math.round(activeCalories),
      suffix: '',
      // No goal: nothing stores an active-calorie target, and 600 was the same
      // number for every user regardless of size, sex or activity.
      goal: null,
      tone: '#d99667',
      icon: <LocalFireDepartment sx={{ color: '#df8a57', fontSize: 18 }} />,
      iconBg: 'rgba(223, 138, 87, 0.12)',
      progress: null,
    },
  ];
  const biometricCards = [
    {
      label: 'Weight',
      value: formatWeightValue(latestBiometric.weight ?? userData?.weight, useMetricSystem),
      delta: formatWeightDelta(
        latestBiometric.weight ?? userData?.weight,
        previousBiometric.weight,
        useMetricSystem
      ),
      dataKey: 'weight',
      color: '#d0b47e',
    },
    {
      label: 'HRV',
      value: latestBiometric.hrv ? `${Math.round(latestBiometric.hrv)} ms` : '—',
      delta: formatDelta(latestBiometric.hrv, previousBiometric.hrv, { percent: true }),
      dataKey: 'hrv',
      color: '#8ec8d7',
    },
    {
      label: 'Resting HR',
      value: latestBiometric.restingHeartRate ? `${Math.round(latestBiometric.restingHeartRate)} bpm` : '—',
      delta: formatDelta(latestBiometric.restingHeartRate, previousBiometric.restingHeartRate, { suffix: ' bpm', invert: true }),
      dataKey: 'restingHeartRate',
      color: '#d58a5f',
    },
    {
      label: 'Sleep',
      value: formatSleepDuration(latestBiometric.sleepMinutes),
      delta: formatDelta(latestBiometric.sleepMinutes, previousBiometric.sleepMinutes, { suffix: ' m' }),
      dataKey: 'sleepMinutes',
      color: '#a6c26f',
    },
  ];
  const recentFoodDisplay = recentFoods.length > 0 ? recentFoods : [...foodEntries].slice(-4).reverse();

  if (!activeSection) {
    return <Navigate to={{ pathname: '/dashboard/daily-state', search: location.search }} replace />;
  }

  const sidebar = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: `linear-gradient(180deg, rgba(6,12,10,0.78), rgba(6,12,10,0.95)), url(${cardMountains})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Spa sx={{ color: tokens.accentStrong, fontSize: 22 }} />
          <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500, letterSpacing: '-0.01em' }}>
            FastTime
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: tokens.textMuted }}>
          Premium daily wellness cockpit
        </Typography>
      </Box>

      <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
        {navItems.map((item) => {
          const isActive = activeSection === item.key;
          return (
          <ListItemButton
            key={item.key}
            selected={isActive}
            onClick={() => navigateToSection(item.key)}
            sx={{
              mb: 0.5,
              minHeight: 40,
              px: 1.25,
              py: 0.35,
              borderRadius: 1.5,
              border: `1px solid ${isActive ? BORDER_STRONG : 'transparent'}`,
              alignItems: 'center',
              '&.Mui-selected': {
                backgroundColor: 'rgba(169, 191, 122, 0.12)',
              },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 30, color: isActive ? tokens.accentStrong : tokens.textMuted }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                color: isActive ? tokens.cream : tokens.textSoft,
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
              }}
            />
          </ListItemButton>
        );
        })}
      </List>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Box
          sx={{
            p: 2,
            border: `1px solid ${BORDER}`,
            backgroundColor: 'rgba(9,14,12,0.62)',
            minHeight: 152,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body1" sx={{ color: tokens.cream, fontStyle: 'italic', fontFamily: editorialSerif, lineHeight: 1.6 }}>
            Discipline is the bridge between goals and accomplishment.
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.accentStrong }}>
            Jim Rohn
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, borderTop: `1px solid ${BORDER}` }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(169,191,122,0.18)', color: tokens.accentStrong }}>
            {friendlyName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>
                {friendlyName}
              </Typography>
              {isPremium && (
                <Chip
                  label="Premium"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    borderRadius: 1,
                    bgcolor: 'rgba(169,191,122,0.16)',
                    color: tokens.accentStrong,
                    border: `1px solid ${BORDER}`,
                  }}
                />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: tokens.textMuted }} noWrap>
              {user?.email}
            </Typography>
          </Box>
          <ChevronRight sx={{ color: tokens.textMuted, fontSize: 18 }} />
        </Stack>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{
            justifyContent: 'center',
            py: 1.1,
            borderRadius: 2,
            fontWeight: 700,
            color: '#fff7ef',
            background: 'linear-gradient(135deg, #d96f53 0%, #a33d2b 100%)',
            boxShadow: '0 16px 30px rgba(122, 37, 22, 0.28)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e07a5f 0%, #b24430 100%)',
              boxShadow: '0 18px 34px rgba(122, 37, 22, 0.34)',
            },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: tokens.gradientHero }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: tokens.accentStrong }} />
          <Typography variant="body2" sx={{ color: tokens.textSoft }}>
            Loading your dashboard...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: BG,
      }}
    >
      {isDesktop ? (
        <Box sx={{ width: RAIL_WIDTH, flexShrink: 0, borderRight: `1px solid ${BORDER}` }}>
          <Box sx={{ position: 'sticky', top: 0, height: '100vh' }}>{sidebar}</Box>
        </Box>
      ) : (
        <Drawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: RAIL_WIDTH,
              backgroundColor: BG,
            },
          }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!isDesktop && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              px: { xs: 2, md: 3 },
              py: 1,
              borderBottom: `1px solid ${BORDER}`,
              backdropFilter: 'blur(22px)',
              backgroundColor: 'rgba(8,17,13,0.84)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: tokens.textSoft }}>
                <Menu />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: tokens.textSoft }}>
                  {getGreeting(friendlyName)}
                </Typography>
                <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500 }}>
                  Small choices. Powerful momentum.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setFoodDialogOpen(true)}
                sx={{
                  ...tonalButtonSx(),
                  px: 2.2,
                  borderRadius: 2,
                  minWidth: 'auto',
                }}
                startIcon={<Add sx={{ fontSize: 16 }} />}
              >
                Log Quick Entry
              </Button>
              <IconButton onClick={() => navigateToSection('support')} sx={{ color: tokens.textSoft, border: `1px solid ${BORDER}`, borderRadius: 2 }} aria-label="Support">
                <NotificationsNone fontSize="small" />
              </IconButton>
              <IconButton onClick={() => navigateToSection('settings')} sx={{ color: tokens.textSoft, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                <Settings fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        )}

        <Box sx={{ px: { xs: 2, md: 2.15 }, py: { xs: 2, md: 2 } }}>
          {previewMode && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{ mb: 2, borderColor: tokens.accentStrong, color: tokens.cream }}
            >
              Sample data — this is a product preview, not a real account.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <DashboardHistoryNavigator
            selectedDate={selectedDate}
            selectedDateLabel={selectedDateLabel}
            historyDays={historyDays}
            onChangeDate={handleSelectedDateChange}
            onStepDate={handleStepSelectedDate}
            loading={loading}
          />

          {activeSection === 'daily-state' ? (
            <UserDashboardHome
              userId={user?.id}
              greeting={getGreeting(friendlyName)}
              displayDate={displayDate}
              selectedDate={selectedDate}
              displayMetric={displayMetric}
              fastingState={fastingState}
              fastingPhases={fastingPhases}
              activeFastingPhaseIndex={activeFastingPhaseIndex}
              estimatedEndText={estimatedEndText}
              fastingStreak={fastingStreak}
              bestFastingStreak={bestFastingStreak}
              isDesktop={isDesktop}
              isWideBoard={isWideBoard}
              dailyCalorieRequirement={dailyCalorieRequirement}
              nutritionMacroCards={nutritionMacroCards}
              mealShowcase={mealShowcase}
              recentFoodDisplay={recentFoodDisplay}
              activityCards={activityCards}
              chartData={chartData}
              biometricCards={biometricCards}
              biometricSeries={biometricSeries}
              reflection={reflection}
              setReflection={setReflection}
              getFoodVisual={getFoodVisual}
              summarizeMealEntries={summarizeMealEntries}
              onLogFood={() => setFoodDialogOpen(true)}
              onViewFoodLog={() => navigateToSection('nutrition')}
              onLogActivity={openHabitsDialog}
              onManageFast={() => setFastingDialogOpen(true)}
              fastingRecommendations={fastingRecommendations}
              onApplySuggestedProtocol={(protocol) => {
                setFastingProtocol(protocol);
                setFastingDialogOpen(true);
              }}
              onOpenSettings={() => navigateToSection('settings')}
              onOpenSupport={() => navigateToSection('support')}
              onQuickAddFood={handleQuickAddRecentFood}
              onSaveReflection={handleSaveReflection}
              onViewBiometrics={() => navigateToSection('biometrics')}
            />
          ) : (
            renderUserAnalyticsScreen(activeSection, {
              metricsRange: weeklyMetrics,
              healthRange: healthDataRange,
              summaryRange,
              userData,
              userId: user?.id,
              nutritionPreferences,
              // Same day record the dashboard ring resolves its goals from
              // (see resolveGoals({ habits, preferences }) above), so the
              // Activity screen's hit-rate agrees with what the ring shows
              // instead of silently falling back to preferences.
              currentHabits: habits,
              onLogFood: () => setFoodDialogOpen(true),
              onLogActivity: openHabitsDialog,
              onManageFast: () => setFastingDialogOpen(true),
              fastingActive: Boolean(todayMetrics?.fasting?.isActive),
              currentProtocol: displayMetric?.fasting?.protocol || fastingProtocol,
              foodEntries: displayMetric?.food?.foodEntries || [],
              selectedDateLabel,
              onDeleteFood: handleRemoveFood,
              deletingFood: updating,
              onSubmitProduct: () => setProductSubmissionOpen(true),
              onNavigateSupport: () => navigateToSection('support'),
              onOpenProductSubmission: () => setProductSubmissionOpen(true),
              onSignOut: handleLogout,
            })
          )}
        </Box>
      </Box>

      <Dialog open={habitsDialogOpen} onClose={() => setHabitsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log activity for {selectedDateLabel}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1 }}>
                Steps
              </Typography>
              <Slider
                value={Number(habitDraft.steps) || 0}
                min={0}
                max={20000}
                step={100}
                onChange={(_, value) => setHabitDraft((current) => ({ ...current, steps: value }))}
              />
              <Typography variant="caption" sx={{ color: tokens.textSoft }}>
                {Number(habitDraft.steps || 0).toLocaleString()} steps
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1 }}>
                Water (liters)
              </Typography>
              <Slider
                value={Number(habitDraft.waterIntake) || 0}
                min={0}
                max={5}
                step={0.1}
                onChange={(_, value) => setHabitDraft((current) => ({ ...current, waterIntake: value }))}
              />
              <Typography variant="caption" sx={{ color: tokens.textSoft }}>
                {(Number(habitDraft.waterIntake) || 0).toFixed(1)} L
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1 }}>
                Exercise (hours)
              </Typography>
              <Slider
                value={Number(habitDraft.exerciseHours) || 0}
                min={0}
                max={4}
                step={0.1}
                onChange={(_, value) => setHabitDraft((current) => ({ ...current, exerciseHours: value }))}
              />
              <Typography variant="caption" sx={{ color: tokens.textSoft }}>
                {(Number(habitDraft.exerciseHours) || 0).toFixed(1)} hours
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHabitsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHabits} disabled={updating}>
            Save activity
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={foodDialogOpen} onClose={() => setFoodDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log food entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Search foods"
              value={foodSearch}
              onChange={(event) => setFoodSearch(event.target.value)}
              placeholder="Salmon, oats, yogurt..."
              fullWidth
            />
            <TextField
              label="Meal type"
              select
              value={foodMealType}
              onChange={(event) => setFoodMealType(event.target.value)}
              fullWidth
            >
              {MEAL_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Quantity"
              type="number"
              value={foodQuantity}
              onChange={(event) => setFoodQuantity(Number(event.target.value))}
              fullWidth
            />
            <TextField
              label="Meal context"
              select
              value={foodContextDraft.mealContext}
              onChange={(event) => setFoodContextDraft((prev) => ({ ...prev, mealContext: event.target.value }))}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {['breaking_fast', 'eating_window', 'pre_fast', 'post_fast', 'free_eating'].map((ctx) => (
                <MenuItem key={ctx} value={ctx}>{ctx.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" sx={{ color: tokens.textMuted }}>Satiety (1–10)</Typography>
            <Slider
              value={foodContextDraft.satietyScore}
              min={1}
              max={10}
              step={1}
              marks
              valueLabelDisplay="auto"
              onChange={(_, value) => setFoodContextDraft((prev) => ({ ...prev, satietyScore: value }))}
            />
            <TextField
              label="Craving trigger (optional)"
              value={foodContextDraft.cravingTrigger}
              onChange={(event) => setFoodContextDraft((prev) => ({ ...prev, cravingTrigger: event.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Preparation"
                select
                value={foodContextDraft.preparationType}
                onChange={(event) => setFoodContextDraft((prev) => ({ ...prev, preparationType: event.target.value }))}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="home_cooked">Home cooked</MenuItem>
                <MenuItem value="packaged">Packaged</MenuItem>
              </TextField>
              <TextField
                label="Dining"
                select
                value={foodContextDraft.diningContext}
                onChange={(event) => setFoodContextDraft((prev) => ({ ...prev, diningContext: event.target.value }))}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="homemade">Homemade</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
              </TextField>
            </Stack>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${BORDER}` }}>
              {foods.map((food) => (
                <Box
                  key={food.id}
                  onClick={() => setSelectedFood(food)}
                  sx={{
                    p: 1.4,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${BORDER}`,
                    backgroundColor: selectedFood?.id === food.id ? 'rgba(169,191,122,0.1)' : 'transparent',
                  }}
                >
                  <Typography variant="body2" sx={{ color: tokens.cream }}>
                    {food.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                    {food.calories} kcal • {food.quantity || 100}{food.unit || 'g'}
                  </Typography>
                </Box>
              ))}
              {foods.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    No foods found for that search yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFoodDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddFood} disabled={!selectedFood || updating}>
            Add food
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fastingDialogOpen} onClose={() => setFastingDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{todayMetrics?.fasting?.isActive ? 'End fasting session' : 'Start fasting session'}</DialogTitle>
        <DialogContent>
          {todayMetrics?.fasting?.isActive ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.textSoft }}>
                End the active {todayMetrics.fasting.protocol} fast and log how it felt.
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>Hunger during fast</Typography>
              <Slider
                value={fastQualityDraft.hungerScore}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, value) => setFastQualityDraft((prev) => ({ ...prev, hungerScore: value }))}
              />
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>Energy during fast</Typography>
              <Slider
                value={fastQualityDraft.energyScore}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, value) => setFastQualityDraft((prev) => ({ ...prev, energyScore: value }))}
              />
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>Focus during fast</Typography>
              <Slider
                value={fastQualityDraft.focusScore}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, value) => setFastQualityDraft((prev) => ({ ...prev, focusScore: value }))}
              />
              <TextField
                select
                fullWidth
                label="Sleep impact"
                value={fastQualityDraft.sleepImpact}
                onChange={(event) => setFastQualityDraft((prev) => ({ ...prev, sleepImpact: event.target.value }))}
              >
                {['none', 'mild', 'moderate', 'severe'].map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Workout impact"
                value={fastQualityDraft.workoutImpact}
                onChange={(event) => setFastQualityDraft((prev) => ({ ...prev, workoutImpact: event.target.value }))}
              >
                {['none', 'mild', 'moderate', 'severe'].map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Reason for ending early (optional)"
                value={fastQualityDraft.reasonEndedEarly}
                onChange={(event) => setFastQualityDraft((prev) => ({ ...prev, reasonEndedEarly: event.target.value }))}
              />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.textSoft }}>
                Choose the window you want to follow next.
              </Typography>
              {fastingRecommendations?.suggestedProtocol && (
                <FastingRecommendationsPanel
                  recommendations={fastingRecommendations}
                  onApplyProtocol={setFastingProtocol}
                />
              )}
              {FASTING_PROTOCOLS.map((protocol) => (
                <Button
                  key={protocol.value}
                  variant={fastingProtocol === protocol.value ? 'contained' : 'outlined'}
                  onClick={() => setFastingProtocol(protocol.value)}
                  sx={{ justifyContent: 'space-between' }}
                >
                  <Box component="span">{protocol.label}</Box>
                  <Box component="span">{protocol.hours} hours</Box>
                </Button>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFastingDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStartOrStopFast} disabled={updating}>
            {todayMetrics?.fasting?.isActive ? 'End Fast' : 'Start Fast'}
          </Button>
        </DialogActions>
      </Dialog>

      <ProductSubmissionDialog
        open={productSubmissionOpen}
        onClose={() => setProductSubmissionOpen(false)}
        onSubmitted={() => showSnackbar('Product submitted for review.')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}
